import DocumentTypes from '@sunbit-dev/common/enums/DocumentTypes';
import IApplicationDetails from '@sunbit-dev/common/models/cardApply/IApplicationDetails';
import ICreditLineDetails from '@sunbit-dev/common/models/cardApply/ICreditLineDetails';
import IExistingPaymentMethod from '@sunbit-dev/common/models/cardApply/IExistingPaymentMethod';
import IInitialDetails from '@sunbit-dev/common/models/cardApply/IInitialDetails';
import ILoginRequest from '@sunbit-dev/common/models/cardApply/ILoginRequest';
import ILoginResponse from '@sunbit-dev/common/models/cardApply/ILoginResponse';
import INewPaymentMethodSubmit from '@sunbit-dev/common/models/cardApply/INewPaymentMethodSubmit';
import IUpdateApplicationDetailsRequest from '@sunbit-dev/common/models/cardApply/IUpdateApplicationDetailsRequest';
import IDocument from '@sunbit-dev/common/models/IDocument';
import IHttpClient from '@sunbit-dev/common/services/httpClient/IHttpClient';
import IAddress from '@sunbit-dev/common/models/IAddress';
import { datadogRum } from '@sunbit-dev/common/services/datadog';
import { CARD_ENV } from '../../utils/helpers/envHelper';
import { IVerificationLinkResponse } from '@sunbit-dev/common/models/cardApply/IVerificationLinkResponse';

export default class SunbitApi {
  private httpClient!: IHttpClient;
  private initialToken: string = '';
  private applicationToken: string = '';
  private applicationId!: number;
  public pendingApplicationId!: number;

  public init(httpClient: IHttpClient) {
    this.httpClient = httpClient;
    return this;
  }

  public isTokenReady(): boolean {
    return Boolean(this.initialToken) || CARD_ENV === 'ci';
  }

  public isApplicationIdReady(): boolean {
    return Boolean(this.applicationId) || CARD_ENV === 'ci';
  }

  public setInitialToken(initialToken: string) {
    this.initialToken = initialToken;
    return this;
  }

  public setApplicationToken(applicationToken: string) {
    this.applicationToken = applicationToken;
    return this;
  }

  public setPendingApplicationId(pendingApplicationId: number | undefined) {
    if (pendingApplicationId) {
      this.pendingApplicationId = pendingApplicationId;
      datadogRum.addRumGlobalContext('pendingApplicationId', pendingApplicationId);
    }
    return this;
  }

  public async generateAppplicationId() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.initialToken}`,
      },
    };
    try {
      const requestData = { pendingAppId: this.pendingApplicationId };
      const response = await this.httpClient.postCard(
        `/card-service/api/v1/card/applications/by-pending-application/${this.pendingApplicationId}`,
        requestData,
        config
      );
      return response.data as any;
    } catch (error) {
      throw error;
    }
  }

  public setApplicationId(applicationId: number) {
    this.applicationId = applicationId;
    datadogRum.addRumGlobalContext('applicationId', applicationId);
    return this;
  }

  public async getInitialDetails() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.initialToken}`,
      },
    };
    const response = await this.httpClient.getCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/preliminary-info`,
      config
    );
    return response.data as IInitialDetails;
  }

  public async login(loginRequest: ILoginRequest) {
    const config = {
      headers: {
        Authorization: `Bearer ${this.initialToken}`,
      },
    };
    try {
      const response = await this.httpClient.postCard(
        `/card-service/api/v1/card/applications/${this.applicationId}/login`,
        loginRequest,
        config
      );
      return response.data as ILoginResponse;
    } catch (error: any) {
      datadogRum.addAction(`card-apply-login-error`, { error_info: { ...error, requestDetails: loginRequest } });
      throw error;
    }
  }

  public async getApplicationDetails() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    const response = await this.httpClient.getCard(
      `/card-service/api/v1/card/applications/${this.applicationId}`,
      config
    );
    return response.data as IApplicationDetails;
  }

  public async getExistingPaymentMethods() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    const response = await this.httpClient.getCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/customer-payment-profiles`,
      config
    );
    return response.data as IExistingPaymentMethod[];
  }

  public async updateApplicationDetails(updateApplicationDetailsRequest: IUpdateApplicationDetailsRequest) {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    await this.httpClient.putCard(
      `/card-service/api/v1/card/applications/${this.applicationId}`,
      updateApplicationDetailsRequest,
      config
    );
  }

  public async applyForCreditLine() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    const response = await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/credit-line`,
      {},
      config
    );
    return response.data as ICreditLineDetails;
  }

  public async acceptOffer() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/offer/accept`,
      {},
      config
    );
  }

  public async assignExistingPaymentMethod(existingPaymentMethod: IExistingPaymentMethod) {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/payment-profiles/existing`,
      existingPaymentMethod,
      config
    );
  }

  public async assignNewPaymentMethod(newPaymentMethodSubmit: INewPaymentMethodSubmit, applicationId: number) {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/payment-profiles/new`,
      { ...newPaymentMethodSubmit, id: applicationId },
      config
    );
  }

  public async setDueDate(dueDate: number) {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/due-date`,
      { dueDate },
      config
    );
  }

  public async setPlasticAddress(address: IAddress) {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    const addressObj: IAddress = {
      state: address.state,
      city: address.city,
      address: address.address,
      zipcode: address.zipcode,
    };
    await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/card-address`,
      addressObj,
      config
    );
  }

  public async applyForCard() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/account`,
      null,
      config
    );
  }

  public getDocument(documentType: DocumentTypes) {
    const body = { documentType };
    return this.httpClient
      .postCard(`/card-service/api/v1/card/applications/${this.applicationId}/document`, body)
      .then((response: any): IDocument => {
        return response.data.fileToDownloadDTO;
      });
  }

  public getStaticDocument(documentType: DocumentTypes) {
    const body = { documentType };
    return this.httpClient
      .postCard(`/card-service/api/v1/card/static-documents`, body)
      .then((response: any): IDocument => {
        return response.data.fileToDownloadDTO;
      });
  }

  public getApplicationDocument(documentType: DocumentTypes) {
    const token =
      documentType === DocumentTypes.CardApplicationSolicitationDisclosures ? this.initialToken : this.applicationToken;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const body = { documentType };
    return this.httpClient
      .postCard(`/card-service/api/v1/card/applications/${this.applicationId}/document`, body, config)
      .then((response: any): IDocument => {
        return response.data.fileToDownloadDTO;
      });
  }

  public async sendVerificationSMSLink() {
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    await this.httpClient.postCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/verify/sms`,
      null,
      config
    );
  }

  public async getVerificationLink() {
    let response: { data: IVerificationLinkResponse };
    const config = {
      headers: {
        Authorization: `Bearer ${this.applicationToken}`,
      },
    };
    response = await this.httpClient.getCard(
      `/card-service/api/v1/card/applications/${this.applicationId}/verify/link`,
      config
    );
    return response.data;
  }
}
